import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RefreshCw, WifiOff, Wifi, Camera, LogOut, Flag, Timer as TimerIcon, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import localforage from 'localforage';
import NotificationBell from '../components/NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MobileTimer = ({ user, onLogout }) => {
  const [serverTimeOffset, setServerTimeOffset] = useState(0);
  const [lastSync, setLastSync] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [serverTime, setServerTime] = useState(null); // Store actual server time
  const [perfTimeAtSync, setPerfTimeAtSync] = useState(null); // performance.now() at sync time
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [selectedTimingType, setSelectedTimingType] = useState('');
  const [carNumber, setCarNumber] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [photoData, setPhotoData] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [syncIntervalHours, setSyncIntervalHours] = useState(1);
  const [timezone, setTimezone] = useState('UTC');
  const [incidentDescription, setIncidentDescription] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [initialSyncDone, setInitialSyncDone] = useState(false);
  // New state for timing capture
  const [capturedTimestamp, setCapturedTimestamp] = useState(null);
  const [startTime, setStartTime] = useState(''); // HH:MM format for stage start
  const [countdownMode, setCountdownMode] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(null);
  const [countdownTargetTime, setCountdownTargetTime] = useState(null);
  const [isSavingStageStart, setIsSavingStageStart] = useState(false);
  const countdownIntervalRef = useRef(null);
  const webcamRef = useRef(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Use Web Worker approach for reliable timing even when screen is off
  useEffect(() => {
    let animationFrameId;
    let lastUpdateTime = performance.now();
    
    // Create a persistent time calculation function
    const updateTime = () => {
      const now = performance.now();
      
      // Use performance.now() to calculate elapsed time - immune to device time changes and screen off
      if (serverTime && perfTimeAtSync !== null) {
        const elapsedMs = now - perfTimeAtSync;
        const newServerTime = new Date(serverTime.getTime() + elapsedMs);
        setCurrentTime(newServerTime);
      } else if (serverTime) {
        // Fallback if perfTimeAtSync not set
        const timeSinceLastUpdate = now - lastUpdateTime;
        setCurrentTime(prev => new Date(prev.getTime() + timeSinceLastUpdate));
      } else {
        setCurrentTime(new Date(Date.now() + serverTimeOffset));
      }
      
      lastUpdateTime = now;
    };
    
    // Use both setInterval and requestAnimationFrame for maximum reliability
    // setInterval continues even when tab is backgrounded (though throttled)
    const intervalId = setInterval(updateTime, 100);
    
    // requestAnimationFrame for smooth updates when visible
    const animate = () => {
      updateTime();
      animationFrameId = requestAnimationFrame(animate);
    };
    animationFrameId = requestAnimationFrame(animate);
    
    // Also handle visibility change to recalculate time when screen comes back on
    const handleVisibilityChange = () => {
      if (!document.hidden && serverTime && perfTimeAtSync !== null) {
        // Recalculate current time based on elapsed performance.now()
        const elapsedMs = performance.now() - perfTimeAtSync;
        const newServerTime = new Date(serverTime.getTime() + elapsedMs);
        setCurrentTime(newServerTime);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(intervalId);
      cancelAnimationFrame(animationFrameId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [serverTimeOffset, serverTime, perfTimeAtSync]);

  useEffect(() => {
    loadLocalData();
    fetchEvents();
    fetchAppSettings();
    // Initial sync is silent (no toast)
    syncWithServer(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOnline && pendingRecords.length > 0) {
      syncPendingRecords();
    }
    // Auto-sync data when coming back online (but not on initial load)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, pendingRecords.length]);

  useEffect(() => {
    if (selectedEvent) {
      fetchStages(selectedEvent.id);
    }
  }, [selectedEvent]);

  // Auto-sync every hour based on app settings
  useEffect(() => {
    if (!syncIntervalHours) return;
    
    const intervalMs = syncIntervalHours * 60 * 60 * 1000; // Convert hours to milliseconds
    const syncInterval = setInterval(() => {
      if (isOnline) {
        syncWithServer();
      }
    }, intervalMs);

    return () => clearInterval(syncInterval);
  }, [syncIntervalHours, isOnline]);

  const loadLocalData = async () => {
    const savedOffset = await localforage.getItem('serverTimeOffset');
    const savedSync = await localforage.getItem('lastSync');
    const savedPending = await localforage.getItem('pendingRecords');
    const savedEvent = await localforage.getItem('selectedEvent');
    const savedStage = await localforage.getItem('selectedStage');
    const savedServerTime = await localforage.getItem('serverTime');
    const savedTimezone = await localforage.getItem('timezone');
    const savedEvents = await localforage.getItem('cachedEvents');
    const savedStages = await localforage.getItem('cachedStages');
    const savedPerfTimeAtSync = await localforage.getItem('perfTimeAtSync');

    if (savedOffset) setServerTimeOffset(savedOffset);
    if (savedSync) setLastSync(new Date(savedSync));
    if (savedPending) setPendingRecords(savedPending);
    if (savedTimezone) setTimezone(savedTimezone);
    
    // Load cached events for offline use
    if (savedEvents && savedEvents.length > 0) {
      setEvents(savedEvents);
    }
    
    // Load cached stages for offline use
    if (savedStages) {
      setStages(savedStages);
    }
    
    // Load selected event after events are loaded
    if (savedEvent) {
      setSelectedEvent(savedEvent);
    }
    
    // Load selected stage
    if (savedStage) {
      setSelectedStage(savedStage);
    }
    
    // Load server time with performance.now() reference
    if (savedServerTime) {
      const serverTimeDate = new Date(savedServerTime.serverTimeAtSync);
      setServerTime(serverTimeDate);
      // Calculate new perfTimeAtSync based on elapsed real time since last sync
      if (savedPerfTimeAtSync && savedServerTime.realTimeAtSync) {
        const realTimeElapsed = Date.now() - savedServerTime.realTimeAtSync;
        setPerfTimeAtSync(performance.now() - realTimeElapsed);
      } else {
        setPerfTimeAtSync(performance.now());
      }
    }
  };

  // Callback for when event changes (triggered by notification)
  const handleEventChange = useCallback(() => {
    fetchEvents();
    toast.info('Event has been updated. Refreshing...');
  }, []);

  const fetchEvents = async () => {
    // If offline, use cached data
    if (!isOnline) {
      const cachedEvents = await localforage.getItem('cachedEvents');
      if (cachedEvents && cachedEvents.length > 0) {
        setEvents(cachedEvents);
        const savedEvent = await localforage.getItem('selectedEvent');
        if (savedEvent) setSelectedEvent(savedEvent);
      }
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const userRole = user.active_role || user.role;
      
      // If user is marshal or scorer, only fetch current event
      if (userRole === 'marshal' || userRole === 'scorer') {
        const response = await axios.get(`${API}/events/current`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data) {
          const eventList = [response.data];
          setEvents(eventList);
          setSelectedEvent(response.data);
          // Cache for offline use
          await localforage.setItem('cachedEvents', eventList);
        } else {
          toast.error('No current event set. Please contact administrator.');
        }
      } else {
        // Admin gets all events
        const response = await axios.get(`${API}/events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const activeEvents = response.data.filter(e => e.is_active);
        setEvents(activeEvents);
        // Cache for offline use
        await localforage.setItem('cachedEvents', activeEvents);
        
        // Auto-select current event or first event
        const currentEvent = response.data.find(e => e.is_current);
        if (currentEvent) {
          setSelectedEvent(currentEvent);
        } else if (response.data.length > 0 && !selectedEvent) {
          setSelectedEvent(response.data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      // Try to use cached data on error
      const cachedEvents = await localforage.getItem('cachedEvents');
      if (cachedEvents && cachedEvents.length > 0) {
        setEvents(cachedEvents);
        toast.info('Using cached events (offline)');
      } else {
        toast.error('Failed to load events');
      }
    }
  };

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/app-settings`);
      setSyncIntervalHours(response.data.sync_interval_hours);
      setTimezone(response.data.timezone || 'UTC');
    } catch (error) {
      console.error('Failed to fetch app settings');
    }
  };

  const fetchStages = async (eventId) => {
    // If offline, use cached data
    if (!isOnline) {
      const cachedStages = await localforage.getItem('cachedStages');
      if (cachedStages) {
        setStages(cachedStages);
      }
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stages?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStages(response.data);
      // Cache for offline use
      await localforage.setItem('cachedStages', response.data);
    } catch (error) {
      console.error('Failed to fetch stages:', error);
      // Try cached data on error
      const cachedStages = await localforage.getItem('cachedStages');
      if (cachedStages) {
        setStages(cachedStages);
      }
    }
  };

  const syncWithServer = async (showToast = true) => {
    if (!isOnline) {
      if (showToast) toast.error('Cannot sync: No internet connection');
      return;
    }

    // Prevent multiple syncs
    if (syncing) return;

    setSyncing(true);
    try {
      const perfTimeBeforeRequest = performance.now();
      const response = await axios.get(`${API}/time/sync`);
      const perfTimeAfterRequest = performance.now();
      
      // Calculate round-trip time and adjust
      const roundTripTime = perfTimeAfterRequest - perfTimeBeforeRequest;
      const estimatedServerTime = new Date(response.data.server_time);
      
      // Adjust for network latency (half of round-trip time)
      const adjustedServerTime = new Date(estimatedServerTime.getTime() + (roundTripTime / 2));
      
      // Store server time and performance.now() reference (immune to device time changes)
      setServerTime(adjustedServerTime);
      setPerfTimeAtSync(performance.now());
      
      // Update timezone from server
      if (response.data.timezone) {
        setTimezone(response.data.timezone);
        await localforage.setItem('timezone', response.data.timezone);
      }
      
      // Also calculate offset for backward compatibility
      const offset = adjustedServerTime.getTime() - Date.now();
      setServerTimeOffset(offset);
      
      const syncTime = new Date();
      setLastSync(syncTime);

      await localforage.setItem('serverTimeOffset', offset);
      await localforage.setItem('serverTime', {
        serverTimeAtSync: adjustedServerTime.toISOString(),
        realTimeAtSync: Date.now()
      });
      await localforage.setItem('perfTimeAtSync', performance.now());
      await localforage.setItem('lastSync', syncTime.toISOString());

      // Only show toast if explicitly requested (not on initial load)
      if (showToast && initialSyncDone) {
        toast.success('Time synchronized successfully');
      }
      setInitialSyncDone(true);
    } catch (error) {
      if (showToast) toast.error('Failed to sync time');
    } finally {
      setSyncing(false);
    }
  };

  const syncPendingRecords = async () => {
    if (pendingRecords.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/timing-records/batch`, pendingRecords, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`${pendingRecords.length} records synced successfully`);
      setPendingRecords([]);
      await localforage.setItem('pendingRecords', []);
    } catch (error) {
      toast.error('Failed to sync pending records');
    }
  };

  const handleTimingButton = (timingType) => {
    setSelectedTimingType(timingType);
    setShowRecordDialog(true);
    setCarNumber('');
    setPhotoData(null);
    setShowCamera(false);
    setCapturedTimestamp(null);
    setStartTime('');
    setCountdownMode(false);
    setCountdownSeconds(null);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  // Capture the current synced time
  const captureTime = () => {
    setCapturedTimestamp(new Date(currentTime));
    toast.success('Time captured!');
  };

  // Check if this timing type needs capture time button
  const needsCaptureTime = (type) => {
    return ['service_in', 'service_out', 'stage_finish', 'stage_arrival'].includes(type);
  };

  // Start countdown for stage start
  const startCountdown = () => {
    if (!carNumber) {
      toast.error('Please enter car number');
      return;
    }
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }
    if (!selectedStage) {
      toast.error('Please select a stage');
      return;
    }
    if (!startTime || !startTime.match(/^\d{2}:\d{2}$/)) {
      toast.error('Please enter a valid start time (HH:MM)');
      return;
    }

    // Parse start time
    const [hours, minutes] = startTime.split(':').map(Number);
    const targetTime = new Date(currentTime);
    targetTime.setHours(hours, minutes, 0, 0);

    // If target time is in the past, assume it's for tomorrow
    if (targetTime <= currentTime) {
      targetTime.setDate(targetTime.getDate() + 1);
    }

    const diffMs = targetTime - currentTime;
    const diffSeconds = Math.ceil(diffMs / 1000);

    if (diffSeconds <= 0) {
      toast.error('Start time must be in the future');
      return;
    }

    if (diffSeconds > 3600) {
      toast.error('Start time must be within 1 hour');
      return;
    }

    setCountdownMode(true);
    setCountdownSeconds(diffSeconds);
    setCountdownTargetTime(targetTime);

    // Start countdown interval
    countdownIntervalRef.current = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Effect to save when countdown reaches zero
  useEffect(() => {
    if (countdownMode && countdownSeconds === 0 && countdownTargetTime && !isSavingStageStart) {
      saveStageStartRecord(countdownTargetTime);
    }
  }, [countdownSeconds, countdownMode, countdownTargetTime, isSavingStageStart]);

  // Save stage start record when countdown ends
  const saveStageStartRecord = async (targetTime) => {
    if (isSavingStageStart) return; // Prevent double save
    setIsSavingStageStart(true);
    
    const record = {
      event_id: selectedEvent.id,
      stage_id: selectedStage.id,
      car_number: carNumber,
      timing_type: 'stage_start',
      timestamp: targetTime.toISOString(),
      recorded_by: user.id,
      recorded_by_name: user.name,
      photo_data: photoData,
      device_id: `device-${user.id}`,
      description: null
    };

    try {
      if (isOnline) {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/timing-records`, record, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Stage start recorded!');
      } else {
        const newPending = [...pendingRecords, record];
        setPendingRecords(newPending);
        await localforage.setItem('pendingRecords', newPending);
        toast.warning('Saved locally - will sync when online');
      }

      setCountdownMode(false);
      setCountdownTargetTime(null);
      setShowRecordDialog(false);
    } catch (error) {
      toast.error('Failed to save record');
      setCountdownMode(false);
    } finally {
      setIsSavingStageStart(false);
    }
  };

  // Format countdown seconds to MM:SS
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Compress image to reduce size - maintains aspect ratio
  const compressImage = (dataUrl, quality = 0.25, maxDimension = 480) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Scale down maintaining aspect ratio
        if (width > height) {
          if (width > maxDimension) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = dataUrl;
    });
  };

  const capturePhoto = async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      // Compress the captured image - low quality for speed
      const compressed = await compressImage(imageSrc, 0.25, 480);
      setPhotoData(compressed);
      setShowCamera(false);
    }
  };

  // Check if we're running in a native app (Capacitor)
  const isNativeApp = () => {
    return window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
  };

  // Use Capacitor Camera for native app only, webcam for web
  const openNativeCamera = async () => {
    setCameraActive(true);
    try {
      const image = await CapacitorCamera.getPhoto({
        quality: 25,  // Very low quality for fast uploads
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        width: 480,  // Smaller width
        height: 360,  // Smaller height
        preserveAspectRatio: true,
        correctOrientation: true
      });
      
      if (image.dataUrl) {
        // Further compress
        const compressed = await compressImage(image.dataUrl, 0.25, 480);
        setPhotoData(compressed);
      }
    } catch (error) {
      console.log('Native camera cancelled or failed');
    } finally {
      setCameraActive(false);
    }
  };

  const handleTakePhoto = async () => {
    // Use native camera only on APK, use webcam on web
    if (isNativeApp()) {
      await openNativeCamera();
    } else {
      // On web, use webcam directly
      setShowCamera(true);
    }
  };

  const saveRecord = async () => {
    if (!carNumber) {
      toast.error('Please enter car number');
      return;
    }

    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }

    // Stage is required
    if (!selectedStage) {
      toast.error('Please select a stage');
      return;
    }

    // Require description for incident reports
    if (selectedTimingType === 'incident_report' && !incidentDescription.trim()) {
      toast.error('Please enter incident description');
      return;
    }

    // For timing types that need captured time, require it
    if (needsCaptureTime(selectedTimingType) && !capturedTimestamp) {
      toast.error('Please capture the time first');
      return;
    }

    // Use captured timestamp if available, otherwise use current time
    const timestampToUse = capturedTimestamp || currentTime;

    const record = {
      event_id: selectedEvent.id,
      stage_id: selectedStage.id,
      car_number: carNumber,
      timing_type: selectedTimingType,
      timestamp: timestampToUse.toISOString(),
      recorded_by: user.id,
      recorded_by_name: user.name,
      photo_data: photoData,
      device_id: `device-${user.id}`,
      description: selectedTimingType === 'incident_report' ? incidentDescription : null
    };

    try {
      if (isOnline) {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/timing-records`, record, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Record saved successfully');
      } else {
        const newPending = [...pendingRecords, record];
        setPendingRecords(newPending);
        await localforage.setItem('pendingRecords', newPending);
        toast.warning('Saved locally - will sync when online');
      }

      setShowRecordDialog(false);
      setIncidentDescription(''); // Clear description after save
    } catch (error) {
      toast.error('Failed to save record');
    }
  };

  const formatTime = (date) => {
    try {
      // Convert to the configured timezone using Intl.DateTimeFormat
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const parts = formatter.formatToParts(date);
      const hour = parts.find(p => p.type === 'hour')?.value || '00';
      const minute = parts.find(p => p.type === 'minute')?.value || '00';
      const second = parts.find(p => p.type === 'second')?.value || '00';
      
      // Get milliseconds
      const ms = date.getMilliseconds().toString().padStart(3, '0');
      
      return `${hour}:${minute}:${second}.${ms}`;
    } catch (error) {
      console.error('Time formatting error:', error);
      return date.toISOString().split('T')[1].substring(0, 12);
    }
  };

  const formatLastSync = (date) => {
    if (!date) return 'Never';
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      return formatter.format(date);
    } catch (error) {
      return date.toLocaleString();
    }
  };

  const timingTypes = [
    { key: 'stage_arrival', label: 'STAGE ARRIVAL', color: 'bg-[#27272a]' },
    { key: 'service_in', label: 'SERVICE IN', color: 'bg-[#27272a]' },
    { key: 'incident_report', label: 'INCIDENT REPORT', color: 'bg-[#ef4444]', hasDescription: true },
    { key: 'service_out', label: 'SERVICE OUT', color: 'bg-[#27272a]' }
  ];

  return (
    <div className="min-h-screen bg-[#09090b] p-4 pb-24">
      {/* Header */}
      <div className="max-w-md mx-auto mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-white flex items-center justify-center gap-2 text-center">
              <TimerIcon className="text-[#facc15] flex-shrink-0" />
              <span className="whitespace-nowrap">RALLY TIMING</span>
            </h1>
            <p className="text-xs text-zinc-400 mono-text">by VOSLOO R</p>
            <p className="text-sm text-zinc-400 mono-text">{user.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell user={user} onEventChange={handleEventChange} />
            <Button
              onClick={onLogout}
              variant="ghost"
              size="icon"
              className="text-zinc-400 hover:text-white"
              data-testid="logout-button"
            >
              <LogOut size={20} />
            </Button>
          </div>
        </div>

        {/* Sync Status */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {syncing ? (
                <div className="status-indicator status-syncing" />
              ) : isOnline ? (
                <div className="status-indicator status-online" />
              ) : (
                <div className="status-indicator status-offline" />
              )}
              <span className="text-sm font-bold uppercase-label text-zinc-300">
                {syncing ? 'SYNCING' : isOnline ? 'ONLINE' : 'OFFLINE'}
              </span>
            </div>
            <Button
              onClick={syncWithServer}
              disabled={syncing || !isOnline}
              size="sm"
              className="bg-[#facc15] text-black hover:bg-[#eab308] h-8 px-3"
              data-testid="sync-button"
            >
              <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
            </Button>
          </div>
          <div className="mono-text text-xs text-zinc-400">
            Last Sync: {formatLastSync(lastSync)}
          </div>
          {pendingRecords.length > 0 && (
            <div className="mt-2 text-xs text-[#facc15] mono-text">
              {pendingRecords.length} records pending sync
            </div>
          )}
        </div>

        {/* Current Time Display */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6 text-center mb-6">
          <div className="text-sm uppercase-label text-zinc-400 mb-2">SYNCED TIME ({timezone})</div>
          <div className="text-4xl font-bold mono-text text-[#facc15]">
            {formatTime(currentTime)}
          </div>
        </div>

        {/* Event Selection */}
        {events.length > 0 && (
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4 mb-4">
            <Label className="text-zinc-300 uppercase-label mb-2 block">Current Event</Label>
            {/* Marshals and Scorers see read-only event display, Admins can select */}
            {(user.active_role || user.role) !== 'admin' ? (
              <div 
                className="w-full bg-[#27272a] border border-[#3b82f6] text-white rounded p-3 flex items-center justify-between"
                data-testid="event-display"
              >
                <span className="font-bold">{selectedEvent?.name || 'No event set'}</span>
                <span className="text-xs text-[#3b82f6] uppercase tracking-wider">Current Event</span>
              </div>
            ) : (
              <select
                value={selectedEvent?.id || ''}
                onChange={(e) => {
                  const event = events.find(ev => ev.id === e.target.value);
                  setSelectedEvent(event);
                  localforage.setItem('selectedEvent', event);
                }}
                className="w-full bg-[#27272a] border-[#27272a] text-white rounded p-2"
                data-testid="event-select"
              >
                {events.map(event => (
                  <option key={event.id} value={event.id}>{event.name}</option>
                ))}
              </select>
            )}
            {stages.length > 0 ? (
              <div className="mt-3">
                <Label className="text-zinc-300 uppercase-label mb-2 block">
                  Stage <span className="text-red-500">*</span>
                </Label>
                <select
                  value={selectedStage?.id || ''}
                  onChange={(e) => {
                    const stage = stages.find(s => s.id === e.target.value);
                    setSelectedStage(stage);
                    // Cache selected stage for offline use
                    localforage.setItem('selectedStage', stage);
                  }}
                  className={`w-full bg-[#27272a] text-white rounded p-2 ${!selectedStage ? 'border-2 border-red-500' : 'border-[#27272a]'}`}
                  data-testid="stage-select"
                >
                  <option value="">-- Select Stage (Required) --</option>
                  {stages.map(stage => (
                    <option key={stage.id} value={stage.id}>
                      Stage {stage.stage_number}: {stage.name}
                    </option>
                  ))}
                </select>
                {!selectedStage && (
                  <p className="text-red-500 text-xs mt-1">Please select a stage before recording</p>
                )}
              </div>
            ) : (
              <div className="mt-3 p-3 bg-red-500/20 border border-red-500 rounded">
                <p className="text-red-400 text-sm">No stages available for this event. Contact administrator.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Timing Buttons - Grid with 4 buttons + 2 larger buttons */}
      <div className="max-w-md mx-auto">
        {/* 2x2 grid with 4 buttons */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {timingTypes.map(type => (
            <Button
              key={type.key}
              onClick={() => handleTimingButton(type.key)}
              className={`${type.color} hover:opacity-80 h-20 sm:h-24 px-2 sm:px-4 text-xs sm:text-sm font-black uppercase tracking-wide sm:tracking-widest text-white border-2 border-transparent active:scale-95 transition-transform whitespace-normal leading-tight`}
              data-testid={`timing-button-${type.key}`}
            >
              <span className="block text-center">{type.label}</span>
            </Button>
          ))}
        </div>

        {/* Stage Start - Full Width, Green */}
        <Button
          onClick={() => handleTimingButton('stage_start')}
          className="w-full mt-2 sm:mt-3 bg-[#22c55e] hover:bg-[#16a34a] h-24 sm:h-28 text-lg sm:text-2xl font-black uppercase tracking-wide sm:tracking-widest text-white border-2 border-transparent active:scale-95 transition-transform"
          data-testid="timing-button-stage_start"
        >
          <Flag className="mr-2 sm:mr-3 flex-shrink-0" size={24} />
          <span>STAGE START</span>
        </Button>

        {/* Stage Finish - Full Width Hero Button, Darker Red */}
        <Button
          onClick={() => handleTimingButton('stage_finish')}
          className="w-full mt-2 sm:mt-3 bg-red-800 hover:bg-red-900 h-24 sm:h-28 text-lg sm:text-2xl font-black uppercase tracking-wide sm:tracking-widest text-white border-2 border-transparent active:scale-95 transition-transform"
          data-testid="timing-button-stage_finish"
        >
          <Flag className="mr-2 sm:mr-3 flex-shrink-0" size={24} />
          <span>STAGE FINISH</span>
        </Button>
      </div>

      {/* Record Dialog */}
      <Dialog open={showRecordDialog} onOpenChange={(open) => {
        // Don't close dialog while camera is active or in countdown mode
        if (!open && (showCamera || cameraActive || countdownMode)) return;
        if (!open && countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        setShowRecordDialog(open);
      }}>
        <DialogContent className={`bg-[#18181b] border-[#27272a] text-white ${countdownMode ? 'w-screen h-screen max-w-none max-h-none rounded-none' : 'w-[95vw] max-w-md max-h-[90vh]'} overflow-y-auto p-4 sm:p-6`} onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          
          {/* Countdown Mode - Full Screen */}
          {countdownMode ? (
            <div className="flex flex-col items-center justify-center h-full min-h-[80vh]">
              <div className="text-center w-full px-4">
                <p className="text-xl sm:text-2xl text-zinc-400 mb-2">CAR #{carNumber}</p>
                <p className="text-lg sm:text-xl text-[#facc15] mb-4 sm:mb-8">STAGE START IN</p>
                <div 
                  className={`font-mono font-black leading-none ${countdownSeconds <= 10 ? 'text-red-500' : 'text-white'}`} 
                  style={{ fontSize: 'clamp(80px, 30vw, 300px)' }}
                >
                  {countdownSeconds}
                </div>
                <p className="text-lg text-zinc-500 mt-2">seconds</p>
                {countdownSeconds <= 0 && (
                  <p className="text-4xl sm:text-6xl text-[#22c55e] mt-4 sm:mt-8 font-black animate-pulse">GO!</p>
                )}
              </div>
              <Button
                onClick={() => {
                  if (countdownIntervalRef.current) {
                    clearInterval(countdownIntervalRef.current);
                    countdownIntervalRef.current = null;
                  }
                  setCountdownMode(false);
                  setCountdownTargetTime(null);
                }}
                variant="outline"
                className="mt-8 sm:mt-12 border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white px-8 py-4"
              >
                CANCEL
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl sm:text-2xl font-black uppercase tracking-wider text-[#facc15]">
                  {selectedTimingType.replace('_', ' ')}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 sm:space-y-5 mt-4 sm:mt-5">
                {/* Car Number */}
                <div className="bg-[#1e1e21] rounded-lg p-3 sm:p-4">
                  <Label className="text-zinc-300 uppercase-label text-sm">Car Number</Label>
                  <Input
                    type="text"
                    value={carNumber}
                    onChange={(e) => setCarNumber(e.target.value)}
                    className="mt-2 bg-[#27272a] border-[#27272a] text-white text-xl sm:text-2xl font-bold mono-text h-12 sm:h-14"
                    placeholder="001"
                    autoFocus
                    data-testid="car-number-input"
                  />
                </div>

                {/* Photo Section */}
                <div className="bg-[#1e1e21] rounded-lg p-3 sm:p-4">
                  {!showCamera && !photoData && (
                    <Button
                      onClick={handleTakePhoto}
                      className="w-full bg-[#27272a] hover:bg-[#3f3f46] text-white h-10 sm:h-12"
                      data-testid="open-camera-button"
                    >
                      <Camera className="mr-2" size={18} />
                      TAKE PHOTO (Optional)
                    </Button>
                  )}

                  {showCamera && (
                    <div className="space-y-3">
                      <Webcam
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        screenshotQuality={0.5}
                        className="w-full rounded"
                        videoConstraints={{ 
                          facingMode: 'environment',
                          width: { ideal: 800 },
                          height: { ideal: 600 },
                          aspectRatio: 4/3
                        }}
                        style={{ objectFit: 'contain' }}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Button
                          onClick={() => setShowCamera(false)}
                          variant="outline"
                          className="border-[#27272a] text-zinc-300 h-10"
                        >
                          CANCEL
                        </Button>
                        <Button
                          onClick={capturePhoto}
                          className="bg-[#facc15] text-black hover:bg-[#eab308] h-10"
                          data-testid="capture-photo-button"
                        >
                          CAPTURE
                        </Button>
                      </div>
                    </div>
                  )}

                  {photoData && (
                    <div className="space-y-3">
                      <img src={photoData} alt="Car" className="w-full rounded max-h-40 sm:max-h-48 object-contain bg-black" />
                      <Button
                        onClick={() => {
                          setPhotoData(null);
                          setShowCamera(true);
                        }}
                        variant="outline"
                        className="w-full border-[#27272a] text-zinc-300 h-10"
                      >
                        RETAKE PHOTO
                      </Button>
                    </div>
                  )}
                </div>

                {/* Description field for Incident Report */}
                {selectedTimingType === 'incident_report' && (
                  <div className="bg-[#1e1e21] rounded-lg p-3 sm:p-4">
                    <Label className="text-zinc-300 uppercase-label text-sm">Incident Description</Label>
                    <textarea
                      value={incidentDescription}
                      onChange={(e) => setIncidentDescription(e.target.value)}
                      className="mt-2 w-full bg-[#27272a] border border-[#27272a] text-white rounded-md p-3 min-h-20 sm:min-h-24 resize-none text-sm sm:text-base"
                      placeholder="Describe the incident..."
                      data-testid="incident-description-input"
                    />
                  </div>
                )}

                {/* CAPTURE TIME button for Service IN, OUT, Stage Finish, Stage Arrival */}
                {needsCaptureTime(selectedTimingType) && (
                  <div className="bg-[#1e1e21] rounded-lg p-3 sm:p-4">
                    {!capturedTimestamp ? (
                      <Button
                        onClick={captureTime}
                        className="w-full bg-[#3b82f6] hover:bg-[#2563eb] text-white h-14 text-lg font-black uppercase tracking-widest"
                        data-testid="capture-time-button"
                      >
                        <Clock className="mr-2" size={24} />
                        CAPTURE TIME
                      </Button>
                    ) : (
                      <div className="text-center">
                        <p className="text-zinc-400 text-sm uppercase">Time Captured</p>
                        <p className="text-3xl font-mono font-bold text-[#22c55e] mt-2">
                          {capturedTimestamp.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </p>
                        <Button
                          onClick={() => setCapturedTimestamp(null)}
                          variant="link"
                          className="text-zinc-500 text-xs mt-3"
                        >
                          Recapture
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Current Time and START TIME input for Stage Start */}
                {selectedTimingType === 'stage_start' && (
                  <div className="bg-[#1e1e21] rounded-lg p-3 sm:p-4 space-y-4">
                    {/* Current Time Display */}
                    <div>
                      <Label className="text-zinc-300 uppercase-label text-sm">Current Time (Synced)</Label>
                      <div className="mt-2 bg-[#27272a] rounded-md p-3 text-center">
                        <p className="text-2xl sm:text-3xl font-mono font-bold text-[#facc15]">
                          {currentTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </p>
                      </div>
                    </div>
                    {/* Start Time Input */}
                    <div>
                      <Label className="text-zinc-300 uppercase-label text-sm">Start Time (HH:MM)</Label>
                      <Input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="mt-2 bg-[#27272a] border-[#27272a] text-white text-xl sm:text-2xl font-bold mono-text h-12 sm:h-14"
                        data-testid="start-time-input"
                      />
                    </div>
                  </div>
                )}

                {/* Save/Start Countdown Button */}
                <div className="pt-2">
                  {selectedTimingType === 'stage_start' ? (
                    <Button
                      onClick={startCountdown}
                      className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-white h-14 text-base sm:text-lg font-black uppercase tracking-widest"
                      data-testid="start-countdown-button"
                    >
                      <TimerIcon className="mr-2" size={20} />
                      START COUNTDOWN
                    </Button>
                  ) : (
                    <Button
                      onClick={saveRecord}
                      className="w-full bg-[#facc15] text-black hover:bg-[#eab308] h-14 text-base sm:text-lg font-black uppercase tracking-widest"
                      data-testid="save-record-button"
                      disabled={needsCaptureTime(selectedTimingType) && !capturedTimestamp}
                    >
                      SAVE RECORD
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileTimer;